/**
 * LiveStudioPage — go live from a phone for anything that is not a church
 * service: a Faith Works TV programme, or an online/hybrid event.
 *
 * Route: /admin/live/:scope/:ident   (scope = fwtv | event)
 *
 * Deliberately the same three taps as the church screen — open, title, go
 * live — and deliberately not a second streaming dashboard. The record each
 * scope keeps is written by that scope's own endpoint; the broadcasting is
 * the shared component.
 */
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Loader2, AlertTriangle } from 'lucide-react';
import { CameraBroadcaster } from '@/components/live/CameraBroadcaster';
import { useLiveTransport } from '@/lib/liveKit';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const token = localStorage.getItem('admin_token')
    || localStorage.getItem('eventflow_token') || localStorage.getItem('token');
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || `Request failed (${res.status})`);
  return body;
};

/** What each scope calls to put its own record on air, and where it is seen. */
const SCOPES = {
  fwtv: {
    label: 'Faith Works TV',
    back: '/admin/fwtv',
    titleLabel: "What's on air?",
    titlePlaceholder: 'Midweek Teaching',
    start: (id) => api('/api/fwtv/admin/live/go-live', {
      method: 'POST',
      body: JSON.stringify({ programme_id: id, use_camera: true }),
    }),
    stop: (id) => api('/api/fwtv/admin/live/end-live', {
      method: 'POST', body: JSON.stringify({ programme_id: id }),
    }),
    name: (id) => api(`/api/fwtv/public/programmes/${id}`).then((p) => p.title),
  },
  event: {
    label: 'event',
    back: '/admin/events',
    titleLabel: 'What is this session?',
    titlePlaceholder: 'Opening session',
    start: (id, title) => api(`/api/events/${id}/live/start`, {
      method: 'POST',
      body: JSON.stringify({ use_camera: true, title }),
    }),
    stop: (id) => api(`/api/events/${id}/live/stop`, { method: 'POST' }),
    name: (id) => api(`/api/events/${id}`).then((e) => e.name || e.title || ''),
  },
};

export default function LiveStudioPage() {
  const { scope, ident } = useParams();
  const qc = useQueryClient();
  const transport = useLiveTransport();
  const rules = SCOPES[scope];

  const { data: name } = useQuery({
    queryKey: ['live-subject', scope, ident],
    queryFn: () => rules.name(ident),
    enabled: !!rules,
    retry: false,
  });

  if (!rules) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 grid place-items-center px-6">
        <p data-testid="unknown-scope">Nothing here can be broadcast.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-4 py-6">
      <div className="max-w-md mx-auto">
        <Link to={rules.back}
              className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 mb-5"
              data-testid="live-studio-back">
          <ArrowLeft className="h-3.5 w-3.5" /> {name || rules.label}
        </Link>

        <div className="mb-5">
          <h1 className="text-2xl font-semibold tracking-tight">Go live</h1>
          <p className="text-sm text-slate-400 mt-1">
            Broadcast straight from this device. Whoever is watching sees it on
            the public page.
          </p>
        </div>

        {transport.loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
          </div>
        ) : !transport.livekit ? (
          <Card className="bg-slate-900 border-slate-800 text-slate-100">
            <CardContent className="p-5 flex gap-3" data-testid="no-livekit">
              <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">No streaming server is set up yet.</p>
                <p className="text-slate-400 mt-1">
                  Broadcasting from a camera needs the platform's streaming
                  server. Until it is configured, paste a watch link from
                  whatever you are streaming on instead.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-slate-900 border-slate-800 text-slate-100">
            <CardContent className="p-5">
              <CameraBroadcaster
                scope={scope}
                ident={ident}
                titleLabel={rules.titleLabel}
                titlePlaceholder={rules.titlePlaceholder}
                note="Relayed by the platform's streaming server, so the audience is not limited by this device."
                onStart={(title) => rules.start(ident, title)}
                onStop={() => rules.stop(ident)}
                onLive={() => qc.invalidateQueries({ queryKey: ['live-subject'] })}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
